import { Button } from "@bcc/components/ui/button";
import { cn } from "@bcc/lib/utils";
import { X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { DataTableBulkActionsProps } from "./data-table.types";
import {
  setBulkActionButtonTestId,
  setBulkActionsBarTestId,
  setClearSelectionButtonTestId,
  setSelectionCountTestId,
} from "./data-table.utils";
import { HeaderSelectionPopover } from "./selection-popover/header-selection-popover";

/**
 * DataTableBulkActions
 *
 * Renders bulk action buttons when rows are selected.
 * Animates in/out using tailwindcss-animate.
 */
export function DataTableBulkActions<T extends object>({
  selectedCount,
  bulkActions,
  rowSelection,
  onClearSelection,
  table,
  setRowSelection,
  maxSelectableRows,
}: Readonly<DataTableBulkActionsProps<T>>) {
  const { t } = useTranslation();

  const clearSelectionText = React.useMemo(() => t("dataTable.bulkActions.clearSelection"), [t]);

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      data-testid={setBulkActionsBarTestId()}
      className={cn(
        "flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border",
        "animate-in fade-in slide-in-from-top-2 duration-200"
      )}
    >
      {/* Selection count — interactive popover when table is available, plain text otherwise */}
      <div data-testid={setSelectionCountTestId()} className="flex items-center gap-1">
        {table && setRowSelection ? (
          <HeaderSelectionPopover table={table} setRowSelection={setRowSelection} maxSelectableRows={maxSelectableRows} />
        ) : (
          <span className="text-sm font-medium">{t("dataTable.bulkActions.rowsSelected", { count: selectedCount })}</span>
        )}
        <Button data-testid={setClearSelectionButtonTestId()} variant="ghost" size="sm" onClick={onClearSelection} className="h-7 px-2">
          <X className="size-4" />
          <span className="sr-only">{clearSelectionText}</span>
        </Button>
      </div>

      {/* Bulk action buttons */}
      <div className="flex items-center gap-2">
        {bulkActions.map((action) => {
          const state = action.state?.(rowSelection);
          const isLoading = state === "loading";
          const isDisabled = state === "disabled";

          return (
            <Button
              key={action.label}
              data-testid={setBulkActionButtonTestId(action.label)}
              variant="outline"
              size="sm"
              onClick={() => action.onClick(rowSelection)}
              disabled={isDisabled || isLoading}
              className="h-8"
            >
              {isLoading ? <span className="animate-spin mr-1">⟳</span> : null}
              {action.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
